import { Calendar, CheckCircle, Clock, DollarSign, Download, Filter, Package, Search, User } from "lucide-react";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Payment.css";

const PAYMENT_API = "https://salathai.co.tz/mrms/Payment.php";
const ORDERS_API = "https://salathai.co.tz/mrms/Orders.php";

export default function Payments() {  // Changed from PaymentManagement to Payments
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [dateFilter, setDateFilter] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    filterType: 'today'
  });
  const [formData, setFormData] = useState({ order_id: "", payment_method: "CASH" });

  const paymentMethods = ["CASH", "M-PESA", "SELECOM", "CRDB BANK", "MIX BY YAS"];

  // Get token from localStorage and add to URL
  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      Swal.fire("Session Expired", "Please login again", "warning");
      return;
    }
    loadAllData();
  }, []);

  useEffect(() => {
    filterPaymentsByDate();
  }, [payments, dateFilter, searchTerm, filterStatus]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPayments(), loadUnpaidOrders()]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    const token = getToken();
    if (!token) return;

    try {
      // Add token to URL - this is the key fix!
      const url = `${PAYMENT_API}?token=${token}`;
      const res = await fetch(url);
      const text = await res.text();
      
      console.log("Payment API Response:", text);
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      
      try {
        const data = JSON.parse(text);
        if (Array.isArray(data)) {
          const mappedPayments = data.map(payment => ({
            id: payment.id,
            order_id: payment.order_id,
            order_number: payment.order_number,
            staff_name: payment.staff_name,
            total_amount: parseFloat(payment.total_amount || 0),
            discount_amount: parseFloat(payment.discount_amount || 0),
            net_amount: parseFloat(payment.net_amount || payment.total_amount || 0),
            payment_method: payment.payment_method,
            status: payment.status === 'PAID' ? 'APPROVED' : (payment.status === 'PENDING' ? 'PENDING' : payment.status),
            created_at: payment.created_at,
            approved_by_name: payment.approved_by_name,
            approved_at: payment.approved_at,
            items: payment.items
          }));
          console.log("Mapped Payments:", mappedPayments);
          setPayments(mappedPayments);
        } else if (data.error) {
          Swal.fire("Error", data.error, "error");
        } else {
          console.error("Unexpected data format:", data);
          setPayments([]);
        }
      } catch (parseError) {
        console.error("JSON Parse Error:", parseError);
        console.error("Response Text:", text);
        Swal.fire("Error", "Invalid response from server", "error");
        setPayments([]);
      }
    } catch (err) {
      console.error("Load Payments Error:", err);
      Swal.fire("Error", "Cannot connect to server", "error");
      setPayments([]);
    }
  };

  const loadUnpaidOrders = async () => {
    const token = getToken();
    if (!token) return;

    try {
      // Add token to URL
      const url = `${ORDERS_API}?admin=true&token=${token}`;
      const res = await fetch(url);
      const text = await res.text();
      
      if (!res.ok) {
        console.error("Orders API Error:", text);
        return;
      }
      
      try {
        const data = JSON.parse(text);
        if (Array.isArray(data)) {
          const unpaidOrders = data.filter(order => 
            (order.status === 'READY' || order.status === 'PREPARING') && 
            order.payment_status !== 'PAID'
          );
          setOrders(unpaidOrders);
        }
      } catch (parseError) {
        console.error("Orders JSON Error:", parseError);
      }
    } catch (err) {
      console.error("Load Orders Error:", err);
    }
  };

  const loadOrderItems = async (orderId) => {
    const token = getToken();
    if (!token) return [];

    try {
      const url = `${ORDERS_API}?id=${orderId}&token=${token}`;
      const res = await fetch(url);
      const text = await res.text();
      
      if (!res.ok) {
        console.error("Order Items Error:", text);
        return [];
      }
      
      try {
        const data = JSON.parse(text);
        if (data && data.items && Array.isArray(data.items)) {
          return data.items;
        }
      } catch (parseError) {
        console.error("Order Items JSON Error:", parseError);
      }
      return [];
    } catch (err) {
      console.error("Load Order Items Error:", err);
      return [];
    }
  };

  const filterPaymentsByDate = () => {
    let filtered = [...payments];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch(dateFilter.filterType) {
      case 'today':
        filtered = payments.filter(payment => {
          if (!payment.created_at) return false;
          const paymentDate = new Date(payment.created_at);
          paymentDate.setHours(0, 0, 0, 0);
          return paymentDate.getTime() === today.getTime();
        });
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        filtered = payments.filter(payment => {
          if (!payment.created_at) return false;
          const paymentDate = new Date(payment.created_at);
          paymentDate.setHours(0, 0, 0, 0);
          return paymentDate.getTime() === yesterday.getTime();
        });
        break;
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        filtered = payments.filter(payment => {
          if (!payment.created_at) return false;
          const paymentDate = new Date(payment.created_at);
          return paymentDate >= weekStart && paymentDate <= weekEnd;
        });
        break;
      case 'thisMonth':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        filtered = payments.filter(payment => {
          if (!payment.created_at) return false;
          const paymentDate = new Date(payment.created_at);
          return paymentDate >= monthStart && paymentDate <= monthEnd;
        });
        break;
      case 'custom':
        if (dateFilter.startDate && dateFilter.endDate) {
          const start = new Date(dateFilter.startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(dateFilter.endDate);
          end.setHours(23, 59, 59, 999);
          filtered = payments.filter(payment => {
            if (!payment.created_at) return false;
            const paymentDate = new Date(payment.created_at);
            return paymentDate >= start && paymentDate <= end;
          });
        }
        break;
      default:
        filtered = payments;
    }

    if (searchTerm) {
      filtered = filtered.filter(payment => 
        payment.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.staff_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.payment_method?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== "ALL") {
      filtered = filtered.filter(payment => payment.status === filterStatus);
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredPayments(filtered);
  };

  const handleFilterChange = (type) => {
    setDateFilter(prev => ({ ...prev, filterType: type }));
  };

  const viewPaymentDetails = async (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
    
    if (payment.order_id) {
      const items = await loadOrderItems(payment.order_id);
      setOrderItems(items);
    }
  };

  const formatCurrency = (amount) => 
    `TZS ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const calculateSummary = () => {
    const totalPayments = filteredPayments.length;
    const totalRevenue = filteredPayments
      .filter(p => p.status === "APPROVED")
      .reduce((sum, p) => sum + Number(p.total_amount || 0), 0);
    const totalPending = filteredPayments
      .filter(p => p.status === "PENDING")
      .reduce((sum, p) => sum + Number(p.total_amount || 0), 0);
    const pendingCount = filteredPayments.filter(p => p.status === "PENDING").length;
    const approvedCount = filteredPayments.filter(p => p.status === "APPROVED").length;

    return { totalPayments, totalRevenue, totalPending, pendingCount, approvedCount };
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    const token = getToken();
    
    if (!formData.order_id) {
      Swal.fire("Error", "Please select an order", "error");
      return;
    }

    if (!token) {
      Swal.fire("Error", "Authentication required", "error");
      return;
    }

    try {
      const res = await fetch(`${PAYMENT_API}?action=create&token=${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid server response");
      }

      if (data.error) {
        Swal.fire("Error", data.error, "error");
      } else {
        Swal.fire("Success", data.message || "Payment submitted", "success");
        setShowCreateModal(false);
        setFormData({ order_id: "", payment_method: "CASH" });
        loadAllData();
      }
    } catch (err) {
      Swal.fire("Error", "Failed to submit payment", "error");
    }
  };

  const approvePayment = async (payment) => {
    const token = getToken();
    if (!token) {
      Swal.fire("Error", "Authentication required", "error");
      return;
    }

    const confirm = await Swal.fire({
      title: "Approve Payment?",
      html: `<div style="text-align: left;">
        <p><strong>Order:</strong> ${payment.order_number}</p>
        <p><strong>Amount:</strong> ${formatCurrency(payment.total_amount)}</p>
        <p><strong>Method:</strong> ${payment.payment_method}</p>
      </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(`${PAYMENT_API}?token=${token}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "approve", id: payment.id }),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid server response");
      }

      if (data.error) {
        Swal.fire("Error", data.error, "error");
      } else {
        Swal.fire("Success", data.message || "Payment approved", "success");
        loadAllData();
      }
    } catch (err) {
      Swal.fire("Error", "Failed to approve payment", "error");
    }
  };

  const generateReceipt = (payment) => {
    let itemsText = "";
    if (orderItems.length > 0) {
      itemsText = "\nItems:\n" + orderItems.map(item => 
        `  ${item.quantity}x ${item.item_name || 'Item'} = ${formatCurrency(item.subtotal)}`
      ).join("\n");
    }
    
    const receiptContent = `
RECEIPT #${payment.id}
Order: ${payment.order_number}
Date: ${formatDateTime(payment.created_at)}
Staff: ${payment.staff_name}
Method: ${payment.payment_method}
Status: ${payment.status}
${itemsText}
--------------------------------
Total Amount: ${formatCurrency(payment.total_amount)}
Discount: ${formatCurrency(payment.discount_amount)}
Net Amount: ${formatCurrency(payment.net_amount || payment.total_amount)}
--------------------------------
Approved By: ${payment.approved_by_name || "Pending"}
Approved At: ${payment.approved_at ? formatDateTime(payment.approved_at) : "N/A"}
--------------------------------
Thank you for your business!
    `.trim();

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${payment.order_number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'APPROVED': return 'payment-status-approved';
      case 'PENDING': return 'payment-status-pending';
      default: return 'payment-status-other';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'APPROVED': return <CheckCircle size={16} />;
      case 'PENDING': return <Clock size={16} />;
      default: return null;
    }
  };

  const summary = calculateSummary();

  const filterStyles = {
    container: {
      background: 'linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)',
      padding: '20px',
      borderRadius: '15px',
      marginBottom: '20px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
    },
    title: {
      color: 'white',
      margin: '0 0 15px 0',
      fontSize: '1.2rem',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      flexWrap: 'wrap',
      marginBottom: '15px'
    },
    filterButton: (isActive) => ({
      padding: '10px 20px',
      border: 'none',
      borderRadius: '25px',
      fontWeight: '600',
      cursor: 'pointer',
      background: isActive ? 'white' : 'rgba(255,255,255,0.2)',
      color: isActive ? '#1a2980' : 'white',
      transition: 'all 0.3s ease',
      boxShadow: isActive ? '0 5px 15px rgba(0,0,0,0.2)' : 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }),
    dateRange: {
      display: 'flex',
      gap: '15px',
      alignItems: 'center',
      flexWrap: 'wrap',
      background: 'rgba(255,255,255,0.1)',
      padding: '15px',
      borderRadius: '10px',
      marginTop: '10px'
    },
    dateInput: {
      padding: '10px 15px',
      border: '1px solid rgba(255,255,255,0.3)',
      borderRadius: '8px',
      background: 'white',
      color: '#333',
      fontSize: '0.95rem',
      flex: 1,
      minWidth: '150px'
    },
    summary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '15px',
      marginTop: '20px'
    },
    summaryCard: (gradient) => ({
      background: gradient || 'rgba(255,255,255,0.95)',
      borderRadius: '12px',
      padding: '15px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
    }),
    summaryLabel: {
      fontSize: '0.85rem',
      color: '#6b7280',
      marginBottom: '5px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    },
    summaryValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1f2937',
      lineHeight: '1.2'
    },
    summarySubtext: {
      fontSize: '0.8rem',
      color: '#9ca3af',
      marginTop: '4px'
    }
  };

  if (loading) {
    return (
      <div className="payment-loading">
        <div className="payment-spinner"></div>
        <p>Loading payments...</p>
      </div>
    );
  }

  return (
    <div className="payment-container">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .filter-button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.2) !important;
        }
      `}</style>

      <div className="payment-header">
        <h1>Payment Management</h1>
        <button className="payment-btn primary" onClick={() => setShowCreateModal(true)}>
          + New Payment
        </button>
      </div>

      {/* Date Filter Section */}
      <div style={filterStyles.container}>
        <h3 style={filterStyles.title}>
          <Calendar size={20} />
          Filter Payments by Date
        </h3>
        
        <div style={filterStyles.buttonGroup}>
          <button
            style={filterStyles.filterButton(dateFilter.filterType === 'today')}
            onClick={() => handleFilterChange('today')}
            className="filter-button-hover"
          >
            <Calendar size={16} />
            Today
          </button>
          <button
            style={filterStyles.filterButton(dateFilter.filterType === 'yesterday')}
            onClick={() => handleFilterChange('yesterday')}
            className="filter-button-hover"
          >
            <Calendar size={16} />
            Yesterday
          </button>
          <button
            style={filterStyles.filterButton(dateFilter.filterType === 'thisWeek')}
            onClick={() => handleFilterChange('thisWeek')}
            className="filter-button-hover"
          >
            <Calendar size={16} />
            This Week
          </button>
          <button
            style={filterStyles.filterButton(dateFilter.filterType === 'thisMonth')}
            onClick={() => handleFilterChange('thisMonth')}
            className="filter-button-hover"
          >
            <Calendar size={16} />
            This Month
          </button>
          <button
            style={filterStyles.filterButton(dateFilter.filterType === 'custom')}
            onClick={() => handleFilterChange('custom')}
            className="filter-button-hover"
          >
            <Filter size={16} />
            Custom Range
          </button>
        </div>

        {dateFilter.filterType === 'custom' && (
          <div style={filterStyles.dateRange}>
            <input
              type="date"
              value={dateFilter.startDate}
              onChange={(e) => setDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
              style={filterStyles.dateInput}
            />
            <span style={{ color: 'white' }}>to</span>
            <input
              type="date"
              value={dateFilter.endDate}
              onChange={(e) => setDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
              style={filterStyles.dateInput}
            />
          </div>
        )}

        <div style={filterStyles.summary}>
          <div style={filterStyles.summaryCard('linear-gradient(135deg, #517c00 0%, #05463c 100%)')}>
            <div style={{ ...filterStyles.summaryLabel, color: 'rgba(255,255,255,0.9)' }}>
              Total Payments
            </div>
            <div style={{ ...filterStyles.summaryValue, color: 'white' }}>{summary.totalPayments}</div>
            <div style={{ ...filterStyles.summarySubtext, color: 'rgba(255,255,255,0.7)' }}>
              in selected period
            </div>
          </div>

          <div style={filterStyles.summaryCard('linear-gradient(135deg, #4a424b 0%, #081f1c 100%)')}>
            <div style={{ ...filterStyles.summaryLabel, color: 'rgba(255,255,255,0.9)' }}>
              <CheckCircle size={14} />
              Approved Revenue
            </div>
            <div style={{ ...filterStyles.summaryValue, color: 'white' }}>{formatCurrency(summary.totalRevenue)}</div>
            <div style={{ ...filterStyles.summarySubtext, color: 'rgba(255,255,255,0.7)' }}>
              {summary.approvedCount} approved payments
            </div>
          </div>

          <div style={filterStyles.summaryCard('linear-gradient(135deg, #36052a4f 0%, #12131246 100%)')}>
            <div style={{ ...filterStyles.summaryLabel, color: 'rgba(238, 229, 231, 0.9)' }}>
              <Clock size={14} />
              Pending Amount
            </div>
            <div style={{ ...filterStyles.summaryValue, color: 'white' }}>{formatCurrency(summary.totalPending)}</div>
            <div style={{ ...filterStyles.summarySubtext, color: 'rgba(255,255,255,0.7)' }}>
              {summary.pendingCount} pending payments
            </div>
          </div>

          <div style={filterStyles.summaryCard('linear-gradient(135deg, #819b0d9d 0%, #5d167915 100%)')}>
            <div style={{ ...filterStyles.summaryLabel, color: 'rgba(255,255,255,0.9)' }}>
              <Package size={14} />
              Avg. Payment
            </div>
            <div style={{ ...filterStyles.summaryValue, color: 'white' }}>
              {formatCurrency(filteredPayments.length > 0 
                ? filteredPayments.reduce((sum, p) => sum + Number(p.total_amount || 0), 0) / filteredPayments.length 
                : 0)}
            </div>
            <div style={{ ...filterStyles.summarySubtext, color: 'rgba(255,255,255,0.7)' }}>
              per transaction
            </div>
          </div>
        </div>
      </div>

      <div className="payment-filters">
        <div className="payment-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by order #, staff, or method..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="payment-filter-select"
        >
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
        </select>
      </div>

      <div className="payment-table-container">
        <table className="payment-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Order #</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Status</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length > 0 ? (
              filteredPayments.map((payment) => (
                <tr key={payment.id}>
                  <td>#{payment.id}</td>
                  <td>{payment.order_number}</td>
                  <td>{formatCurrency(payment.total_amount)}</td>
                  <td>{payment.payment_method}</td>
                  <td>
                    <span className={`payment-status ${getStatusClass(payment.status)}`}>
                      {getStatusIcon(payment.status)}
                      {payment.status}
                    </span>
                  </td>
                  <td>{formatDate(payment.created_at)}</td>
                  <td className="payment-actions">
                    <button className="payment-action-btn view" onClick={() => viewPaymentDetails(payment)}>
                      View
                    </button>
                    {payment.status === 'PENDING' && (
                      <button className="payment-action-btn approve" onClick={() => approvePayment(payment)}>
                        Approve
                      </button>
                    )}
                    <button className="payment-action-btn receipt" onClick={() => generateReceipt(payment)}>
                      <Download size={14} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="payment-empty">No payments found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals remain the same... */}
      {showCreateModal && (
        <div className="payment-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Create Payment</h3>
              <button className="payment-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreatePayment}>
              <div className="payment-form-group">
                <label>Select Order *</label>
                <select
                  value={formData.order_id}
                  onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                  required
                >
                  <option value="">Choose order...</option>
                  {orders.map((order) => (
                    <option key={order.id} value={order.id}>
                      #{order.order_number} - {formatCurrency(order.total_amount)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="payment-form-group">
                <label>Payment Method *</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  required
                >
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div className="payment-form-actions">
                <button type="button" className="payment-btn secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="payment-btn primary">
                  Submit Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && selectedPayment && (
        <div className="payment-modal-overlay" onClick={() => {
          setShowModal(false);
          setOrderItems([]);
        }}>
          <div className="payment-modal large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h3>Payment Details</h3>
              <button className="payment-modal-close" onClick={() => {
                setShowModal(false);
                setOrderItems([]);
              }}>×</button>
            </div>
            
            <div className="payment-details-grid">
              <div className="payment-detail-section">
                <h4><User size={16} /> Payment Information</h4>
                <div className="payment-detail-row">
                  <span>Payment ID:</span>
                  <span>#{selectedPayment.id}</span>
                </div>
                <div className="payment-detail-row">
                  <span>Order Number:</span>
                  <span>{selectedPayment.order_number}</span>
                </div>
                <div className="payment-detail-row">
                  <span>Staff:</span>
                  <span>{selectedPayment.staff_name}</span>
                </div>
                <div className="payment-detail-row">
                  <span>Status:</span>
                  <span className={getStatusClass(selectedPayment.status)}>
                    {selectedPayment.status}
                  </span>
                </div>
                <div className="payment-detail-row">
                  <span>Payment Method:</span>
                  <span>{selectedPayment.payment_method}</span>
                </div>
                <div className="payment-detail-row">
                  <span>Created:</span>
                  <span>{formatDateTime(selectedPayment.created_at)}</span>
                </div>
                {selectedPayment.approved_by_name && selectedPayment.status === 'APPROVED' && (
                  <>
                    <div className="payment-detail-row">
                      <span>Approved By:</span>
                      <span>{selectedPayment.approved_by_name}</span>
                    </div>
                    {selectedPayment.approved_at && (
                      <div className="payment-detail-row">
                        <span>Approved At:</span>
                        <span>{formatDateTime(selectedPayment.approved_at)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="payment-detail-section">
                <h4><DollarSign size={16} /> Amount Details</h4>
                <div className="payment-detail-row">
                  <span>Total Amount:</span>
                  <span>{formatCurrency(selectedPayment.total_amount)}</span>
                </div>
                <div className="payment-detail-row">
                  <span>Discount:</span>
                  <span>{formatCurrency(selectedPayment.discount_amount)}</span>
                </div>
                <div className="payment-detail-row total">
                  <span>Net Amount:</span>
                  <span>{formatCurrency(selectedPayment.net_amount || selectedPayment.total_amount)}</span>
                </div>
              </div>

              {orderItems.length > 0 && (
                <div className="payment-detail-section items-section">
                  <h4><Package size={16} /> Order Items ({orderItems.length})</h4>
                  <div className="payment-items-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderItems.map((item, index) => (
                          <tr key={index}>
                            <td>{item.item_name || 'Unknown Item'}</td>
                            <td>{item.quantity}</td>
                            <td>{formatCurrency(item.price)}</td>
                            <td>{formatCurrency(item.subtotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3"><strong>Total Items:</strong></td>
                          <td><strong>{orderItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="payment-form-actions">
              <button className="payment-btn primary" onClick={() => generateReceipt(selectedPayment)}>
                <Download size={16} /> Download Receipt
              </button>
              <button className="payment-btn secondary" onClick={() => {
                setShowModal(false);
                setOrderItems([]);
              }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}